import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../../components/Seo";
import StatusBadge from "../../components/StatusBadge";
import { LoadingState, ErrorState, EmptyState } from "../../components/StateBlocks";
import { formatEventDate } from "../../utils/dateTime";
import useAuth from "../../hooks/useAuth";
import api, { ApiError } from "../../utils/api";

export default function AdminDashboard() {
  const { idToken, email, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .adminListEvents(idToken)
      .then((data) => setEvents(data.events || []))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Unable to load events."))
      .finally(() => setLoading(false));
  }, [idToken]);

  const stats = {
    total: events.length,
    confirmed: events.filter((e) => e.status === "CONFIRMED").length,
    planned: events.filter((e) => e.status === "PLANNED").length,
    totalConfirmedTeens: events.reduce((sum, e) => sum + (e.confirmedTeenCount || 0), 0),
  };

  return (
    <>
      <Seo title="Admin Dashboard" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
        <h1>Admin dashboard</h1>
        <div>
          {email && <span className="field-hint">Signed in as {email}</span>}{" "}
          <button type="button" className="btn btn-secondary" onClick={logout}>
            Sign out
          </button>
        </div>
      </div>

      <div className="card-grid">
        <div className="card">
          <h2>{stats.total}</h2>
          <p>Total events</p>
        </div>
        <div className="card">
          <h2>{stats.confirmed}</h2>
          <p>Confirmed events</p>
        </div>
        <div className="card">
          <h2>{stats.planned}</h2>
          <p>Planned events</p>
        </div>
        <div className="card">
          <h2>{stats.totalConfirmedTeens}</h2>
          <p>Confirmed teen RSVPs (across all events)</p>
        </div>
      </div>

      <div className="form-field">
        <Link className="btn btn-primary" to="/admin/events/new">
          + New event
        </Link>{" "}
        <Link className="btn btn-secondary" to="/admin/audit">
          View audit log
        </Link>
      </div>

      {loading && <LoadingState label="Loading events\u2026" />}
      {error && <ErrorState message={error} />}
      {!loading && !error && events.length === 0 && <EmptyState title="No events yet" />}
      {!loading && !error && events.length > 0 && (
        <table className="data-table">
          <caption className="visually-hidden">All events</caption>
          <thead>
            <tr>
              <th scope="col">Title</th>
              <th scope="col">Status</th>
              <th scope="col">Date</th>
              <th scope="col">Capacity</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.eventId}>
                <td>{event.title}</td>
                <td>
                  <StatusBadge status={event.status} />
                </td>
                <td>{formatEventDate(event.startAt)}</td>
                <td>
                  {event.confirmedTeenCount}/{event.capacity}
                </td>
                <td>
                  <Link to={`/admin/events/${event.eventId}`}>Manage</Link>{" "}
                  <Link to={`/admin/events/${event.eventId}/edit`}>Edit</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
